import { useEffect, useState } from 'react';
import { useNet } from '../net/status';

/*
    Shown only when the mail server is unreachable. The wording is the point:
    during the nightly backup it says exactly what is happening and that mail is
    coming straight back, because the alternative — an empty mailbox and no
    explanation at 4 AM — is genuinely alarming.
*/

function ago(since: number, now: number): string {
    const s = Math.max(0, Math.round((now - since) / 1000));
    if (s < 60) return `${s}s`;
    const m = Math.round(s / 60);
    return m < 60 ? `${m} min` : `${Math.round(m / 60)}h`;
}

export default function ConnectionBanner() {
    const { online, since, planned, lastError } = useNet();
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (online) return;
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, [online]);

    if (online) return null;

    // A backup that has run long past its window is no longer routine.
    const overrun = planned && since !== null && now - since > 5 * 60 * 1000;
    const tone = planned && !overrun ? 'var(--accent)' : 'var(--amber)';
    const bg = planned && !overrun ? 'var(--accent-bg)' : 'var(--amber-bg)';

    return (
        <div
            role="status"
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '9px 14px',
                background: bg,
                borderBottom: `1px solid ${tone}`,
                color: 'var(--text)',
                fontSize: 'var(--fs-sm)',
                flex: '0 0 auto',
            }}
        >
            <span
                style={{
                    width: 8,
                    height: 8,
                    flex: '0 0 auto',
                    borderRadius: 'var(--r-pill)',
                    background: tone,
                }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
                {planned && !overrun ? (
                    <>
                        <strong>Nightly backup is running.</strong> Your mail is safe — the server is
                        paused for a few seconds at 04:00 every night and comes straight back. Nothing
                        has been lost; new mail is queued by the senders and arrives once it's done.
                    </>
                ) : planned ? (
                    <>
                        <strong>The nightly backup hasn't finished.</strong> It normally takes about ten
                        seconds and it's been {since ? ago(since, now) : 'a while'}. Nothing is lost, but
                        it's worth checking the server.
                    </>
                ) : (
                    <>
                        <strong>Can't reach the mail server.</strong> Retrying automatically
                        {since ? ` — ${ago(since, now)} so far` : ''}. Incoming mail isn't lost: sending
                        servers retry for days.
                    </>
                )}
                {!planned && lastError ? (
                    <span className="kicker" style={{ display: 'block', marginTop: 2 }}>
                        // {lastError}
                    </span>
                ) : null}
            </div>
            <button
                onClick={() => location.reload()}
                style={{
                    height: 26,
                    padding: '0 11px',
                    flex: '0 0 auto',
                    background: 'transparent',
                    border: `1px solid ${tone}`,
                    borderRadius: 'var(--r-btn)',
                    color: 'var(--text)',
                    fontSize: 'var(--fs-sm)',
                }}
            >
                Retry
            </button>
        </div>
    );
}
